import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AuthpopupComponent, AuthpopupContext } from 'src/app/shared/components/authpopup/authpopup.component';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { ReferenceRequestContext, ReferenceRequestService } from '../reference-request.service';

export interface ReferenceRequestDialogData {
  // Draft to start from (the hero prompt, or what the user typed before closing the dialog).
  brief?: string;
  context: ReferenceRequestContext;
}

export type ReferenceRequestStep = 'form' | 'sending' | 'sent';

// "New-reference request" — the brief form from the boss's prototype (app.js requestForm /
// requestStatus), on our popup surface. Guests write the brief first and sign in on top of it
// (the auth popup opens over this dialog, the draft stays); after signing in the request still
// needs an explicit "Send request" — the prototype's handoff rule, so nothing is submitted by
// surprise. The parent reads `brief` / `sent` on close to keep or drop the draft.
@Component({
  selector: 'app-reference-request-dialog',
  templateUrl: './reference-request-dialog.component.html',
  styleUrls: ['./reference-request-dialog.component.scss'],
})
export class ReferenceRequestDialogComponent {
  public static readonly BRIEF_MAX_LENGTH = 600;

  public readonly maxLength = ReferenceRequestDialogComponent.BRIEF_MAX_LENGTH;
  public readonly placeholder = 'A medieval harbor, focusing on timber architecture and working props…';

  public brief: string;
  public step: ReferenceRequestStep = 'form';
  public errorMessage = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: ReferenceRequestDialogData,
    private readonly dialogRef: MatDialogRef<ReferenceRequestDialogComponent>,
    private readonly dialog: MatDialog,
    private readonly firebaseAuth: FirebaseAuthService,
    private readonly requests: ReferenceRequestService,
  ) {
    this.brief = (data.brief || '').slice(0, this.maxLength);
  }

  public get isSignedIn(): boolean {
    return !!this.firebaseAuth.currentUser;
  }

  public get replyEmail(): string {
    return this.firebaseAuth.currentUser?.email || '';
  }

  public get sent(): boolean {
    return this.step === 'sent';
  }

  public get canSubmit(): boolean {
    return this.step === 'form' && this.brief.trim().length > 0;
  }

  public get submitLabel(): string {
    if (this.step === 'sending') {
      return 'Sending…';
    }
    return this.isSignedIn ? 'Send request' : 'Continue to sign in';
  }

  public onSubmit(event?: Event): void {
    event?.preventDefault();
    if (!this.canSubmit) {
      return;
    }

    if (!this.isSignedIn) {
      this.openSignIn();
      return;
    }

    this.send();
  }

  public close(): void {
    this.dialogRef.close();
  }

  // The auth popup opens over this dialog; when it closes the getters re-read the auth state,
  // so the button turns into "Send request" and the brief is still there.
  private openSignIn(): void {
    // Prototype's account-popup reason for a request.
    const data: AuthpopupContext = {
      title: 'Keep track of your request.',
      text: 'Follow your brief and receive an email when reviewed references are ready.',
    };
    this.dialog.open(AuthpopupComponent, { data, restoreFocus: false, panelClass: 'webpopup-overlay' });
  }

  private async send(): Promise<void> {
    this.step = 'sending';
    this.errorMessage = '';

    try {
      await this.requests.submit(this.brief.trim(), this.data.context);
      this.step = 'sent';
    } catch (error) {
      console.error('Reference request failed', error);
      this.step = 'form';
      this.errorMessage = 'Couldn’t send the request. Please try again.';
    }
  }
}
